import { useState, useMemo, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient, ApiError } from '../lib/api';
import { useAuth } from '../store/auth';
import { Card } from '../components/ui/Card';
import { KpiCard } from '../components/ui/Card';
import { Panel } from '../components/ui/Panel';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { SectionHeader } from '../components/ui/SectionHeader';
import { CountryFlag } from '../components/ui/CountryFlag';
import { SubIdLightning } from '../components/SubIdLightning';
import { Activity, Search, Bot, AlertTriangle, Eye, Pause, Play, Zap } from 'lucide-react';
import { formatRelativeTime, formatCurrency, getDeviceLabel, formatNumber, cn } from '../lib/utils';
import { useOfferLookup, useCampaignLookup } from '../hooks/useLookups';
import { SubDetailDrawer } from '../components/subdetail/SubDetailDrawer';
import { usePollMultiplier } from '../hooks/usePollMultiplier';

// ──────────────────────────────────────────────────────────────────────
// Types (same as original)
// ──────────────────────────────────────────────────────────────────────

interface LiveClick {
  timestamp: string;
  click_id: string;
  sub_id: string;
  campaign_slug: string;
  offer_id_str: string;
  country: string;
  isp: string;
  device: string;
  os: string;
  source: string;
  ip: string;
  user_agent: string;
  destination_url: string;
  hostname: string;
  fraud_flags: string;
  is_unique: number;
  is_bot: number;
  bot_score: number;
  is_datacenter: number;
  campaign_id: number;
  offer_id: number;
  conversion: { revenue: number; payout: number; status: string } | null;
}

// ──────────────────────────────────────────────────────────────────────
// Helpers (IP mask, bot check, utils)
// ──────────────────────────────────────────────────────────────────────

/** Mask last two octets of IP: 192.168.1.1 → 192.168.•••.••• */
function maskIp(ip: string | null | undefined): string {
  const clean = (ip ?? '').trim();
  if (!clean) return '•••.•••.•••.•••';
  const parts = clean.split('.');
  if (parts.length === 4) {
    return `${parts[0]}.${parts[1]}.•••.•••`;
  }
  return clean; // fallback for invalid IPs
}

function isBotClick(row: LiveClick) {
  return Number(row.is_bot) === 1;
}

function DeviceIcon({ device }: { device: string | null | undefined }) {
  const d = (device ?? '').toLowerCase();
  if (d.includes('bot')) return <Bot size={12} className="inline-block text-neon-orange" />;
  if (d.includes('mobile')) return <Activity size={12} className="inline-block text-neon-cyan" />;
  if (d.includes('tablet')) return <Activity size={12} className="inline-block text-neon-purple" />;
  if (d.includes('desktop')) return <Activity size={12} className="inline-block text-neon-green" />;
  return <Activity size={12} className="inline-block text-text-tertiary" />;
}

// ──────────────────────────────────────────────────────────────────────
// Main Page Component
// ──────────────────────────────────────────────────────────────────────

export function ClicksPage() {
  const { user } = useAuth();
  const offerLookup = useOfferLookup();
  const campaignLookup = useCampaignLookup();
  const pollMult = usePollMultiplier();

  // ─── State ─────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'quality' | 'bots'>('all');
  const [pageSize, setPageSize] = useState<number>(50);
  const [streamPaused, setStreamPaused] = useState(false);
  // Sub Detail drawer target (sub_id) — pengganti alert(JSON) yang kriptik
  const [detailSubId, setDetailSubId] = useState<string | undefined>(undefined);

  const lastRealtimeTsRef = useRef<number>(0);
  const realtimeAccumRef = useRef<LiveClick[]>([]);

  // ─── Query: AE clicks (historical, ~30-60s lag) ───────────────────────
  const list = useQuery({
    queryKey: ['clicks-live', filterMode, pageSize],
    queryFn: () =>
      apiClient.get<LiveClick[]>('/api/v1/clicks/live', {
        since_days: 1,
        only_bots: filterMode === 'bots' ? 1 : undefined,
        exclude_bots: filterMode === 'quality' ? 1 : undefined,
        limit: pageSize,
      }),
    refetchInterval: streamPaused ? false : 15_000 * pollMult,
    retry: false,
  });

  // ─── Query: DO realtime stream (sub-2s lag) ──────────────────────────
  const realtime = useQuery({
    queryKey: ['clicks-realtime', pageSize],
    queryFn: async () => {
      const delta = await apiClient.get<LiveClick[]>('/api/v1/clicks/realtime', {
        limit: pageSize,
        since: lastRealtimeTsRef.current,
      });
      for (const cl of delta) {
        const t = new Date((cl.timestamp || '').replace(' ', 'T') + 'Z').getTime();
        if (Number.isFinite(t) && t > lastRealtimeTsRef.current) {
          lastRealtimeTsRef.current = t;
        }
      }
      const seen = new Set<string>();
      const merged: LiveClick[] = [];
      for (const r of [...delta, ...realtimeAccumRef.current]) {
        if (!r.click_id || seen.has(r.click_id)) continue;
        seen.add(r.click_id);
        merged.push(r);
      }
      merged.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
      realtimeAccumRef.current = merged.slice(0, pageSize);
      return realtimeAccumRef.current;
    },
    refetchInterval: streamPaused ? false : 10_000 * pollMult,
    retry: false,
    placeholderData: (prev) => prev,
  });

  // ─── Error states ────────────────────────────────────────────────────
  const aeError = list.error as ApiError | undefined;
  const aeNotConfigured = aeError?.code === 'AE_NOT_CONFIGURED';
  const aeRateLimited = aeError?.code === 'AE_RATE_LIMITED';

  // ─── Merge + Filter logic (preserve existing behavior) ───────────────
  const merged = useMemo(() => {
    const aeRows = list.data ?? [];
    const rtRows = realtime.data ?? [];
    if (rtRows.length === 0) {
      return [...aeRows].sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
    }
    const seen = new Set<string>();
    const out: LiveClick[] = [];
    for (const r of rtRows) {
      if (seen.has(r.click_id)) continue;
      seen.add(r.click_id);
      out.push(r);
    }
    for (const r of aeRows) {
      if (seen.has(r.click_id)) continue;
      seen.add(r.click_id);
      out.push(r);
    }
    let filtered = out;
    if (filterMode === 'bots') filtered = filtered.filter(isBotClick);
    else if (filterMode === 'quality') filtered = filtered.filter((r) => !isBotClick(r));
    filtered.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
    return filtered.slice(0, pageSize);
  }, [list.data, realtime.data, filterMode, pageSize]);

  const filtered = useMemo(() => {
    const rows = merged;
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const campaignName = campaignLookup.nameFromSlug(r.campaign_slug);
      const offerName = offerLookup.getName(r.offer_id || null);
      return (
        (r.sub_id ?? '').toLowerCase().includes(q) ||
        (r.click_id ?? '').toLowerCase().includes(q) ||
        (r.country ?? '').toLowerCase().includes(q) ||
        (r.campaign_slug ?? '').toLowerCase().includes(q) ||
        campaignName.toLowerCase().includes(q) ||
        offerName.toLowerCase().includes(q) ||
        (r.source ?? '').toLowerCase().includes(q) ||
        (r.isp ?? '').toLowerCase().includes(q) ||
        (r.ip ?? '').toLowerCase().includes(q)
      );
    });
  }, [merged, search, campaignLookup, offerLookup]);

  // ─── Counts ──────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const all = filtered.length;
    const bots = filtered.filter(isBotClick).length;
    return { all, bots, quality: all - bots };
  }, [filtered]);

  // ─── Top campaigns (from current filtered dataset) ────────────────────
  const topCampaigns = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    for (const r of filtered) {
      const name = campaignLookup.nameFromSlug(r.campaign_slug);
      const existing = map.get(r.campaign_slug) || { name, count: 0 };
      map.set(r.campaign_slug, { ...existing, count: existing.count + 1 });
    }
    return Array.from(map.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [filtered, campaignLookup]);

  // ─── Bot pressure (%) ────────────────────────────────────────────────
  const botPressure = useMemo(() => {
    if (counts.all === 0) return 0;
    return Math.round((counts.bots / counts.all) * 100);
  }, [counts.all, counts.bots]);

  // ─── Fraud flags extractor (blob14) ─────────────────────────────────
  const fraudFlags = useMemo(() => {
    const flags = new Set<string>();
    for (const r of filtered) {
      if (r.fraud_flags) {
        const parsed = JSON.parse(r.fraud_flags) as Record<string, unknown>;
        for (const key of Object.keys(parsed)) {
          if (parsed[key] === true || parsed[key] === 1) {
            flags.add(key);
          }
        }
      }
    }
    return Array.from(flags);
  }, [filtered]);

  // ─── Row pulse indicator (first row only) ───────────────────────────
  const isFirstRowPulse = filtered.length > 0 && !streamPaused;

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <SectionHeader
        title="CLICKS LIVE"
        subtitle={streamPaused ? 'STREAM PAUSED' : 'sub-2s stream via Durable Objects'}
        actions={
          <div className="flex items-center gap-2">
            {/* Liveness dot kept next to the control that toggles the stream */}
            <span
              className={cn(
                'w-2 h-2 rounded-full',
                streamPaused ? 'bg-neon-yellow' : 'bg-neon-cyan animate-pulse',
              )}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStreamPaused(!streamPaused)}
              icon={streamPaused ? <Play size={14} /> : <Pause size={14} />}
            >
              {streamPaused ? 'RESUME STREAM' : 'PAUSE STREAM'}
            </Button>
          </div>
        }
      />

      {/* AE Not Configured Warning */}
      {aeNotConfigured && (
        <Panel level={2}>
          <div className="flex items-start gap-2 px-4 py-3 border border-neon-yellow/40 bg-neon-yellow/5 rounded-sm">
            <AlertTriangle size={16} className="text-neon-yellow mt-0.5 shrink-0" />
            <div className="text-xs font-mono text-neon-yellow leading-relaxed">
              {user?.role === 'admin' ? (
                <>
                  <strong>Analytics Engine not configured.</strong> Set{' '}
                  <code className="text-neon-cyan">CLOUDFLARE_ACCOUNT_ID</code> and{' '}
                  <code className="text-neon-cyan">CLOUDFLARE_ANALYTICS_TOKEN</code> on API worker.
                </>
              ) : (
                <>
                  <strong>Click stream unavailable.</strong> Realtime data temporarily inaccessible. Try again in a few minutes.
                </>
              )}
            </div>
          </div>
        </Panel>
      )}

      {/* AE Rate Limited Hint */}
      {aeRateLimited && (
        <Panel level={2}>
          <div className="flex items-center gap-2 px-3 py-1.5 border border-neon-yellow/30 bg-neon-yellow/5 rounded-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-neon-yellow animate-pulse" />
            <p className="text-[10px] font-mono text-neon-yellow/80">
              Analytics throttled — retrying in {Math.round((15_000 * pollMult) / 1000)}s
            </p>
          </div>
        </Panel>
      )}

      {/* KPI Strip (4 columns) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard
          label="CLICKS TODAY"
          value={formatNumber(counts.all)}
          icon={<Activity size={16} />}
          accent="cyan"
        />
        <KpiCard
          label="UNIQUE"
          value={formatNumber(counts.all - counts.bots)}
          icon={<Bot size={16} />}
          accent="green"
        />
        <KpiCard
          label="BOTS"
          value={formatNumber(counts.bots)}
          delta={{ value: `${botPressure}%`, positive: botPressure < 20 }}
          icon={<Bot size={16} />}
          accent="orange"
        />
        <KpiCard
          label="FRESHNESS"
          value={streamPaused ? '—' : '<2s'}
          icon={<Zap size={16} />}
          accent={streamPaused ? 'yellow' : 'cyan'}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex-1 max-w-xs">
          <Input
            placeholder="Search Sub ID / Campaign / Offer / Country / IP..."
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            icon={<Search size={14} />}
          />
        </div>
        <div className="inline-flex bg-bg-secondary border border-border-subtle rounded-sm p-1">
          <button
            onClick={() => setFilterMode('all')}
            className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm ${
              filterMode === 'all' ? 'bg-neon-cyan/10 text-neon-cyan' : 'text-text-secondary'
            }`}
          >
            ALL ({counts.all})
          </button>
          <button
            onClick={() => setFilterMode('quality')}
            className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm ${
              filterMode === 'quality' ? 'bg-neon-green/10 text-neon-green' : 'text-text-secondary'
            }`}
          >
            QUALITY ({counts.quality})
          </button>
          <button
            onClick={() => setFilterMode('bots')}
            className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm ${
              filterMode === 'bots' ? 'bg-neon-orange/10 text-neon-orange' : 'text-text-secondary'
            }`}
          >
            BOTS ({counts.bots})
          </button>
        </div>
      </div>

      {/* Main Content: Table + Right Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left/Middle: Main Feed Table (7 col) */}
        <Panel level={1} className="lg:col-span-2">
          <div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between">
            <h3 className="font-display text-sm uppercase tracking-wider text-neon-cyan flex items-center gap-2">
              <Activity size={14} />
              Feed • TIME · GEO · CAMPAIGN · OFFER · DEVICE · IP · UA · BOT · AKSI
              {list.isRefetching && !streamPaused && (
                <span className="ml-2 text-[10px] text-text-tertiary font-mono">syncing...</span>
              )}
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="table-cyber">
              <thead>
                <tr>
                  <th>TIME</th>
                  <th>GEO</th>
                  <th>CAMPAIGN</th>
                  <th>OFFER</th>
                  <th>DEVICE</th>
                  <th>IP</th>
                  <th>UA</th>
                  <th>BOT</th>
                  <th>AKSI</th>
                </tr>
              </thead>
              <tbody>
                {!list.isLoading && filtered.map((row, idx) => {
                  const isBot = isBotClick(row);
                  const rowStyle = idx === 0 && isFirstRowPulse ? 'pl-3 border-l-2 border-neon-cyan/60' : '';
                  return (
                    <tr key={row.click_id} className={rowStyle}>
                      <td className="font-mono text-[10px] text-text-secondary whitespace-nowrap" title={row.timestamp}>
                        {formatRelativeTime(new Date(
                          /[TZ+]/.test(row.timestamp)
                            ? row.timestamp
                            : row.timestamp.replace(' ', 'T') + 'Z'
                        ).getTime())}
                      </td>
                      <td>
                        {row.country ? (
                          <CountryFlag iso2={row.country} showCode size={16} />
                        ) : (
                          <span className="text-text-tertiary">—</span>
                        )}
                      </td>
                      <td className="font-mono text-[10px] text-neon-cyan whitespace-nowrap">
                        {campaignLookup.nameFromSlug(row.campaign_slug)}
                      </td>
                      <td className="font-mono text-[10px] text-text-secondary">
                        {offerLookup.getName(row.offer_id || null)}
                      </td>
                      <td>
                        <span className="flex items-center gap-1.5 font-mono text-[10px]">
                          <DeviceIcon device={row.device} />
                          <span className="capitalize">{getDeviceLabel(row.device)}</span>
                        </span>
                      </td>
                      <td className="font-mono text-[10px] text-text-tertiary">{maskIp(row.ip)}</td>
                      <td className="font-mono text-[10px] text-text-secondary truncate max-w-[120px]" title={row.user_agent}>
                        {row.user_agent?.slice(0, 40) || '—'}{row.user_agent?.length ? '…' : ''}
                      </td>
                      <td>
                        {isBot ? (
                          <Badge variant="error" pulse>
                            <Bot size={10} className="mr-1" /> BOT
                          </Badge>
                        ) : (
                          <Badge variant="info" pulse>CLEAN</Badge>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => setDetailSubId(row.sub_id || undefined)}
                          className="text-neon-cyan hover:text-neon-cyan/70 transition-colors"
                          title="Lihat detail Sub ID"
                        >
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {!list.isLoading && !list.error && filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-14 text-text-tertiary">
                      <Activity className="mx-auto mb-3 opacity-30" size={36} />
                      <p className="font-mono text-xs">
                        {search ? 'no results matching your filter' : 'no clicks in this time range — fire some traffic'}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          {filtered.length > 0 && (
            <div className="px-5 py-3 border-t border-border-subtle text-[10px] font-mono text-text-tertiary">
              showing {filtered.length} records
            </div>
          )}

          {/* Bottom Strip */}
          <div className="px-5 py-2 border-t border-border-subtle bg-bg-tertiary/50">
            <p className="text-[10px] font-mono text-text-tertiary text-center">
              retention 90d · AE historical lag 30-60s · feed ini realtime DO-backed
            </p>
          </div>
        </Panel>

        {/* Right Sidebar (3-4 col): TOP CAMPAIGNS, BOT PRESSURE, FRAUD FLAGS */}
        <div className="w-full space-y-2">
          {/* TOP CAMPAIGNS NOW */}
          <Panel level={2}>
            <div className="px-4 py-3 border-b border-border-subtle">
              <h4 className="font-display text-xs uppercase tracking-wider text-neon-cyan flex items-center gap-2">
                <Activity size={12} />
                TOP CAMPAIGNS NOW
              </h4>
            </div>
            <div className="p-4 space-y-2">
              {topCampaigns.length === 0 ? (
                <p className="text-xs text-text-tertiary">— no campaigns yet —</p>
              ) : (
                topCampaigns.map((c, idx) => (
                  <div key={idx} className="flex items-center justify-between text-[11px]">
                    <span className="truncate max-w-[180px]">{c.name}</span>
                    <span className="font-mono text-neon-cyan tabular-nums">{c.count}</span>
                  </div>
                ))
              )}
            </div>
          </Panel>

          {/* BOT PRESSURE GAUGE */}
          <Panel level={2}>
            <div className="px-4 py-3 border-b border-border-subtle">
              <h4 className="font-display text-xs uppercase tracking-wider text-neon-orange flex items-center gap-2">
                <Bot size={12} />
                BOT PRESSURE
              </h4>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-1.5">
                <div className="relative w-24 h-24">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    {/* Background arc */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#1f2933"
                      strokeWidth="12"
                    />
                    {/* Progress arc */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke={botPressure < 30 ? '#06b6d4' : botPressure < 60 ? '#a855f7' : '#f97316'}
                      strokeWidth="12"
                      strokeDasharray={`${(botPressure / 100) * 251.2} 251.2`}
                      transform="rotate(-90 50 50)"
                      className="transition-all duration-500"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-display font-bold tabular-numeric" style={{ color: botPressure < 30 ? '#06b6d4' : botPressure < 60 ? '#a855f7' : '#f97316' }}>
                      {botPressure}%
                    </span>
                  </div>
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-text-secondary">CLEAN</span>
                    <span className="font-mono text-neon-green">{100 - botPressure}%</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-text-secondary">BOT</span>
                    <span className="font-mono text-neon-orange">{botPressure}%</span>
                  </div>
                </div>
              </div>
            </div>
          </Panel>

          {/* FRAUD FLAGS (conditional render) */}
          {fraudFlags.length > 0 && (
            <Panel level={2}>
              <div className="px-4 py-3 border-b border-border-subtle">
                <h4 className="font-display text-xs uppercase tracking-wider text-neon-red flex items-center gap-2">
                  <AlertTriangle size={12} />
                  FRAUD FLAGS
                </h4>
              </div>
              <div className="p-4 flex flex-wrap gap-2">
                {fraudFlags.map((flag, idx) => (
                  <Badge key={idx} variant="error" className="text-[10px]">
                    {flag}
                  </Badge>
                ))}
              </div>
            </Panel>
          )}
        </div>
      </div>

      <SubDetailDrawer
        publisherId={detailSubId === undefined ? undefined : null}
        subIdHint={detailSubId}
        onClose={() => setDetailSubId(undefined)}
        range="today"
        customFrom=""
        customTo=""
      />
    </div>
  );
}

